import { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { useFacilityPayments } from '../hooks/use_facility_payments';

function FacilityPaymentsTab({
    cashPayments,
    refreshPayments,
    fetchUnpaidInvoices,
    fetchInvoiceDetails,
    formatCurrency: propFormatCurrency
}) {
    const [globalFilter, setGlobalFilter] = useState('');
    const [paymentFilters, setPaymentFilters] = useState({
        startDate: '',
        endDate: ''
    });
    const toast = useRef(null);

    const {
        states: {
            selectedPayment,
            approveDialogVisible,
            rejectDialogVisible,
            voidDialogVisible,
            overpaymentDialogVisible,
            comments,
            loading,
            unpaidInvoices,
            selectedInvoices,
            overpaymentAmount,
            remainingOverpayment,
            loadingInvoices
        },
        setters: {
            setSelectedPayment,
            setApproveDialogVisible,
            setRejectDialogVisible,
            setVoidDialogVisible,
            setOverpaymentDialogVisible,
            setComments,
            setUnpaidInvoices,
            setSelectedInvoices,
            setOverpaymentAmount,
            setRemainingOverpayment,
            setLoadingInvoices,
            setLoading
        },
        handlers: {
            handleApprovePayment,
            handleRejectPayment,
            handleVoidPayment,
            handleApplyOverpayment,
            handleInvoiceSelect,
            handlePrepareOverpayment,
        },
        utils: {
            formatCurrency: hookFormatCurrency,
            formatDate,
            formatPaymentMethod
        }
    } = useFacilityPayments(refreshPayments, fetchUnpaidInvoices, fetchInvoiceDetails);

    const formatCurrency = propFormatCurrency || hookFormatCurrency;

    // Filter payments by date range
    const filterPaymentsByDate = (payments) => {
        if (!paymentFilters.startDate && !paymentFilters.endDate) {
            return payments;
        }

        return payments.filter(payment => {
            const paymentDate = new Date(payment.paymentDate);
            const startDate = paymentFilters.startDate ? new Date(paymentFilters.startDate) : null;
            const endDate = paymentFilters.endDate ? new Date(paymentFilters.endDate) : null;

            if (startDate && paymentDate < startDate) return false;
            if (endDate && paymentDate > endDate) return false;

            return true;
        });
    };

    // Export to CSV function
    const exportToCSV = (data) => {
        if (!data || data.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'No Data',
                detail: 'No payments to export',
                life: 3000
            });
            return;
        }

        // CSV Headers
        const headers = ['Invoice Number', 'Client Name', 'Amount', 'Date', 'Status'];
        
        // CSV Rows
        const rows = data.map(payment => {
            const clientName = `${payment.client?.firstName || ''} ${payment.client?.lastName || ''}`.trim();
            const amount = formatCurrency(payment.paymentAmount, payment.currency?.code);
            const date = formatDate(payment.paymentDate);
            const invoiceNumber = payment.invoice?.invoiceNumber || 'N/A';
            const status = payment.approvalStatus || 'N/A';

            return [
                invoiceNumber,
                clientName,
                amount,
                date,
                status
            ];
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `facility_payments_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.current?.show({
            severity: 'success',
            summary: 'Export Successful',
            detail: `Exported ${data.length} payment(s) to CSV`,
            life: 3000
        });
    };

    // Filter payments based on status and date
    const allPayments = filterPaymentsByDate(cashPayments || []);
    const pendingPayments = allPayments.filter(payment => payment.approvalStatus === 'Pending');
    const approvedPayments = allPayments.filter(payment => payment.approvalStatus === 'Approved');
    const rejectedPayments = allPayments.filter(payment => payment.approvalStatus === 'Rejected');
    const voidedPayments = allPayments.filter(payment => payment.isVoided === true);

    // Get current active tab data for export
    const getCurrentTabData = () => {
        const activeTab = document.querySelector('.nav-link.active')?.id;
        switch(activeTab) {
            case 'pending-tab': return pendingPayments;
            case 'approved-tab': return approvedPayments;
            case 'rejected-tab': return rejectedPayments;
            case 'voided-tab': return voidedPayments;
            default: return allPayments;
        }
    };

    const statusTemplate = (rowData) => {
        let statusClass = '';
        switch (rowData.approvalStatus) {
            case 'Approved':
                statusClass = 'bg-success';
                break;
            case 'Rejected':
                statusClass = 'bg-danger';
                break;
            default:
                statusClass = 'bg-warning';
                break;
        }

        return (
            <span className={`badge ${statusClass}`}>
                {rowData.approvalStatus}
            </span>
        );
    };

    const paymentMethodTemplate = (rowData) => {
        return formatPaymentMethod(rowData.paymentMethod);
    };

    const paymentActionTemplate = (rowData) => {
        const isPending = rowData.approvalStatus === 'Pending';
        const isVoided = rowData.isVoided;
        const isApproved = rowData.approvalStatus === 'Approved';
        const isOverpaid = rowData.reconciliationStatus === 'Overpaid';

        const showOverpayButton = isApproved && isOverpaid && !isVoided;

        return (
            <div className="actions" style={{ display: 'flex', gap: '8px' }}>
                {isPending && !isVoided && (
                    <>
                        <Button
                            icon="ti ti-check"
                            className="p-button-rounded p-button-success"
                            style={{ margin: '0 4px' }}
                            onClick={() => {
                                setSelectedPayment(rowData);
                                setApproveDialogVisible(true);
                            }}
                            tooltip="Approve Payment"
                            disabled={loading}
                        />
                        <Button
                            icon="ti ti-x"
                            className="p-button-rounded p-button-danger"
                            style={{ margin: '0 4px' }}
                            onClick={() => {
                                setSelectedPayment(rowData);
                                setRejectDialogVisible(true);
                            }}
                            tooltip="Reject Payment"
                            disabled={loading}
                        />
                    </>
                )}

                {showOverpayButton && (
                    <Button
                        icon="ti ti-exchange"
                        className="p-button-rounded p-button-info"
                        style={{ margin: '0 4px' }}
                        onClick={async () => {
                            try {
                                setLoading(true);
                                await handlePrepareOverpayment(
                                    rowData,
                                    fetchInvoiceDetails,
                                    fetchUnpaidInvoices
                                );
                            } catch (error) {
                                console.error('Error preparing overpayment dialog:', error);
                            } finally {
                                setLoading(false);
                            }
                        }}
                        tooltip="Apply Overpayment"
                        disabled={loading}
                    />
                )}

                {!isVoided && (
                    <Button
                        icon="ti ti-trash"
                        className="p-button-rounded p-button-warning"
                        style={{ margin: '0 4px' }}
                        onClick={() => {
                            setSelectedPayment(rowData);
                            setVoidDialogVisible(true);
                        }}
                        tooltip="Void Payment"
                        disabled={loading}
                    />
                )}
            </div>
        );
    };

    const renderDataTable = (data) => (
        <div className="card">
            <div className="card-body">
                <div className="table-responsive">
                    <DataTable
                        value={data}
                        emptyMessage="No payments found"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        stripedRows
                        globalFilter={globalFilter}
                        filterDelay={300}
                        className="payment-table"
                    >
                        <Column field="paymentReference" header="Payment Ref" />
                        <Column field="invoice.invoiceNumber" header="Invoice Number" />
                        <Column
                            field="client.firstName"
                            header="Client"
                            body={(rowData) => `${rowData.client.firstName} ${rowData.client.lastName}`}
                        />
                        <Column
                            field="paymentAmount"
                            header="Amount"
                            body={(rowData) => formatCurrency(rowData.paymentAmount, rowData.currency.code)}
                        />
                        <Column field="receiptNumber" header="Receipt No." />
                        <Column
                            field="paymentDate"
                            header="Date"
                            body={(rowData) => formatDate(rowData.paymentDate)}
                        />
                        <Column
                            field="paymentMethod"
                            header="Payment Method"
                            body={paymentMethodTemplate}
                        />
                        <Column
                            field="approvalStatus"
                            header="Status"
                            body={statusTemplate}
                        />
                        <Column header="Actions" body={paymentActionTemplate} style={{ width: '200px' }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );

    return (
        <div className="tab-pane" id="facility-payments">
            <Toast ref={toast} />

            <div className="card">
                <div className="card-body">
                    {/* Date Filters and Export Section */}
                    <div className="row mb-3 align-items-center">
                        <div className="col-md-2">
                            <div className="input-group">
                                <span className="input-group-text">Start</span>
                                <input 
                                    className="form-control" 
                                    type="date" 
                                    value={paymentFilters.startDate}
                                    onChange={(e) => setPaymentFilters({...paymentFilters, startDate: e.target.value})}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="input-group">
                                <span className="input-group-text">End</span>
                                <input 
                                    className="form-control" 
                                    type="date" 
                                    value={paymentFilters.endDate}
                                    onChange={(e) => setPaymentFilters({...paymentFilters, endDate: e.target.value})}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-secondary w-100"
                                onClick={() => setPaymentFilters({ startDate: '', endDate: '' })}
                                disabled={!paymentFilters.startDate && !paymentFilters.endDate}
                            >
                                <i className="ti ti-filter-off me-1"></i>
                                Clear Filters
                            </button>
                        </div>
                        <div className="col-md-6 text-end">
                            <button
                                className="btn btn-primary"
                                onClick={() => exportToCSV(getCurrentTabData())}
                            >
                                <i className="ti ti-download me-1"></i>
                                Export to CSV
                            </button>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="mb-3 d-flex align-items-center justify-content-between w-100">
                            <ul className="nav nav-tabs invoice-tab border-bottom" id="paymentTabs" role="tablist">
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link active"
                                        id="all-tab"
                                        data-bs-toggle="tab"
                                        data-bs-target="#all-tab-pane"
                                        type="button"
                                        role="tab"
                                        aria-controls="all-tab-pane"
                                        aria-selected="true"
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            All <span className="avtar rounded-circle bg-light-primary">{allPayments.length}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link"
                                        id="pending-tab"
                                        data-bs-toggle="tab"
                                        data-bs-target="#pending-tab-pane"
                                        type="button"
                                        role="tab"
                                        aria-controls="pending-tab-pane"
                                        aria-selected="false"
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            Pending <span className="avtar rounded-circle bg-light-warning">{pendingPayments.length}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link"
                                        id="approved-tab"
                                        data-bs-toggle="tab"
                                        data-bs-target="#approved-tab-pane"
                                        type="button"
                                        role="tab"
                                        aria-controls="approved-tab-pane"
                                        aria-selected="false"
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            Approved <span className="avtar rounded-circle bg-light-success">{approvedPayments.length}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link"
                                        id="rejected-tab"
                                        data-bs-toggle="tab"
                                        data-bs-target="#rejected-tab-pane"
                                        type="button"
                                        role="tab"
                                        aria-controls="rejected-tab-pane"
                                        aria-selected="false"
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            Rejected <span className="avtar rounded-circle bg-light-danger">{rejectedPayments.length}</span>
                                        </span>
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link"
                                        id="voided-tab"
                                        data-bs-toggle="tab"
                                        data-bs-target="#voided-tab-pane"
                                        type="button"
                                        role="tab"
                                        aria-controls="voided-tab-pane"
                                        aria-selected="false"
                                    >
                                        <span className="d-flex align-items-center gap-2">
                                            Voided <span className="avtar rounded-circle bg-light-secondary">{voidedPayments.length}</span>
                                        </span>
                                    </button>
                                </li>
                            </ul>

                            <span className="p-input-icon-left">
                                <i className="pi pi-search" />
                                <InputText
                                    value={globalFilter}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Search payments..."
                                    className="form-control"
                                    style={{ width: '250px' }}
                                />
                            </span>
                        </div>
                    </div>
                    <div className="tab-content" id="paymentTabsContent">
                        <div
                            className="tab-pane fade show active"
                            id="all-tab-pane"
                            role="tabpanel"
                            aria-labelledby="all-tab"
                            tabIndex="0"
                        >
                            {renderDataTable(allPayments)}
                        </div>
                        <div
                            className="tab-pane fade"
                            id="pending-tab-pane"
                            role="tabpanel"
                            aria-labelledby="pending-tab"
                            tabIndex="0"
                        >
                            {renderDataTable(pendingPayments)}
                        </div>
                        <div
                            className="tab-pane fade"
                            id="approved-tab-pane"
                            role="tabpanel"
                            aria-labelledby="approved-tab"
                            tabIndex="0"
                        >
                            {renderDataTable(approvedPayments)}
                        </div>
                        <div
                            className="tab-pane fade"
                            id="rejected-tab-pane"
                            role="tabpanel"
                            aria-labelledby="rejected-tab"
                            tabIndex="0"
                        >
                            {renderDataTable(rejectedPayments)}
                        </div>
                        <div
                            className="tab-pane fade"
                            id="voided-tab-pane"
                            role="tabpanel"
                            aria-labelledby="voided-tab"
                            tabIndex="0"
                        >
                            {renderDataTable(voidedPayments)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Approve Payment Dialog */}
            <Dialog
                header="Approve Payment"
                visible={approveDialogVisible}
                onHide={() => {
                    setApproveDialogVisible(false);
                    setComments('');
                }}
                className="w-50"
            >
                <div className="p-3">
                    <div className="mb-4">
                        <div className="row">
                            <div className="col-md-6">
                                <p>
                                    <strong>Payment Reference:</strong> {selectedPayment?.paymentReference}
                                </p>
                                <p>
                                    <strong>Payment Amount:</strong> {selectedPayment ? formatCurrency(selectedPayment.paymentAmount, selectedPayment.currency.code) : ''}
                                </p>
                            </div>
                            <div className="col-md-6">
                                <p>
                                    <strong>Payment Method:</strong> {selectedPayment ? formatPaymentMethod(selectedPayment.paymentMethod) : ''}
                                </p>
                                <p>
                                    <strong>Receipt Number:</strong> {selectedPayment?.receiptNumber}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label">Comments (Optional)</label>
                        <InputTextarea
                            className="form-control"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={4}
                            autoResize
                        />
                    </div>

                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary mr-2"
                            onClick={() => {
                                setApproveDialogVisible(false);
                                setComments('');
                            }}
                            style={{ marginRight: '10px' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleApprovePayment}
                            disabled={loading}
                        >
                            Approve Payment
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* Reject Payment Dialog */}
            <Dialog
                header="Reject Payment"
                visible={rejectDialogVisible}
                onHide={() => {
                    setRejectDialogVisible(false);
                    setComments('');
                }}
                className="w-50"
            >
                <div className="p-3">
                    <div className="mb-4">
                        <label className="form-label">Reason for Rejection <span className="text-danger">*</span></label>
                        <InputTextarea
                            className="form-control"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={4}
                            autoResize
                            required
                        />
                    </div>

                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary mr-2"
                            onClick={() => {
                                setRejectDialogVisible(false);
                                setComments('');
                            }}
                            style={{ marginRight: '10px' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleRejectPayment}
                            disabled={loading || !comments.trim()}
                        >
                            Reject Payment
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* Void Payment Dialog */}
            <Dialog
                header="Void Payment"
                visible={voidDialogVisible}
                onHide={() => {
                    setVoidDialogVisible(false);
                    setComments('');
                }}
                className="w-50"
            >
                <div className="p-3">
                    <div className="mb-4">
                        <label className="form-label">Reason for Voiding <span className="text-danger">*</span></label>
                        <InputTextarea
                            className="form-control"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={4}
                            autoResize
                            required
                        />
                    </div>

                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-secondary mr-2"
                            onClick={() => {
                                setVoidDialogVisible(false);
                                setComments('');
                            }}
                            style={{ marginRight: '10px' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning"
                            onClick={handleVoidPayment}
                            disabled={loading || !comments.trim()}
                        >
                            Void Payment
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* Overpayment Dialog */}
            <Dialog
                header="Apply Overpayment to Unpaid Invoices"
                visible={overpaymentDialogVisible}
                onHide={() => {
                    setOverpaymentDialogVisible(false);
                    setSelectedInvoices([]);
                    setUnpaidInvoices([]);
                    setOverpaymentAmount(0);
                    setRemainingOverpayment(0);
                }}
                className="w-75"
            >
                <div className="p-3">
                    {loadingInvoices ? (
                        <div className="text-center">
                            <span className="spinner-border text-primary"></span>
                            <p className="mt-2">Loading invoices...</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p>
                                    <strong>Client:</strong> {selectedPayment?.client?.firstName} {selectedPayment?.client?.lastName}
                                </p>
                                <p>
                                    <strong>Overpayment Amount:</strong> {formatCurrency(overpaymentAmount, selectedPayment?.currency?.code || 'USD')}
                                </p>
                                <p>
                                    <strong>Remaining Amount:</strong> {formatCurrency(remainingOverpayment, selectedPayment?.currency?.code || 'USD')}
                                </p>
                            </div>

                            <div className="mb-4">
                                <h6>Select Invoices to Apply Overpayment:</h6>

                                {!Array.isArray(unpaidInvoices) ? (
                                    <div className="alert alert-warning">
                                        Error: Invalid invoice data format. Please try again.
                                    </div>
                                ) : unpaidInvoices.length === 0 ? (
                                    <div className="alert alert-info">
                                        No unpaid invoices found for this client.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px' }}></th>
                                                    <th>Invoice Number</th>
                                                    <th>Issue Date</th>
                                                    <th>Due Date</th>
                                                    <th>Amount</th>
                                                    <th>Paid</th>
                                                    <th>Balance</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {unpaidInvoices.map((invoice) => {
                                                    const amountToPay = (invoice.totalAmount || 0) - (invoice.amountPaid || 0);
                                                    const canSelect = amountToPay <= remainingOverpayment + (selectedInvoices.find(inv => inv._id === invoice._id) ? amountToPay : 0);

                                                    return (
                                                        <tr key={invoice._id || `invoice-${Math.random()}`} className={canSelect ? '' : 'text-muted'}>
                                                            <td>
                                                                <Checkbox
                                                                    checked={selectedInvoices.some(inv => inv._id === invoice._id)}
                                                                    onChange={(e) => handleInvoiceSelect(e, invoice)}
                                                                    disabled={!canSelect && !selectedInvoices.some(inv => inv._id === invoice._id)}
                                                                />
                                                            </td>
                                                            <td>{invoice.invoiceNumber || 'N/A'}</td>
                                                            <td>{formatDate(invoice.issueDate) || 'N/A'}</td>
                                                            <td>{formatDate(invoice.dueDate) || 'N/A'}</td>
                                                            <td>{formatCurrency(invoice.totalAmount || 0, invoice.currency?.code)}</td>
                                                            <td>{formatCurrency(invoice.amountPaid || 0, invoice.currency?.code)}</td>
                                                            <td>{formatCurrency(amountToPay, invoice.currency?.code)}</td>
                                                            <td>
                                                                <span className={`badge ${invoice.status === 'Overdue' ? 'bg-danger' : 'bg-warning'}`}>
                                                                    {invoice.status || 'Unknown'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary mr-2"
                                    onClick={() => {
                                        setOverpaymentDialogVisible(false);
                                        setSelectedInvoices([]);
                                        setUnpaidInvoices([]);
                                        setOverpaymentAmount(0);
                                        setRemainingOverpayment(0);
                                    }}
                                    style={{ marginRight: '10px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleApplyOverpayment}
                                    disabled={loading || selectedInvoices.length === 0}
                                >
                                    Apply Overpayment
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default FacilityPaymentsTab;